/**
 * Side-panel table for the Funnel tab. Shows each attribution channel with
 * signup count + share-of-total + a Cedar-colored stripe of share.
 */
import React from 'react';
import { CEDAR } from '../theme';
import { formatInt, formatPercent } from '../format';

export interface ChannelRow {
  channel: string;
  signups: number;
  // null when there are zero total signups (no denominator).
  share_pct: number | null;
}

export interface ChannelBreakdownTableProps {
  rows: ChannelRow[];
  totalSignups: number;
}

const CHANNEL_LABELS: Record<string, string> = {
  organic: 'Organic',
  direct: 'Direct',
  paid_meta: 'Paid · Meta',
  paid_google: 'Paid · Google',
  paid_tiktok: 'Paid · TikTok',
  content_seo: 'Content / SEO',
  referral: 'Referral',
  unknown: 'Unknown',
  all: 'All channels',
};

export const ChannelBreakdownTable: React.FC<ChannelBreakdownTableProps> = ({
  rows,
  totalSignups,
}) => {
  if (!rows.length) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-gray-500">
        No signup attribution recorded yet.
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline justify-between text-[11px]">
        <span className="text-gray-500">Channel</span>
        <span className="text-gray-500">
          <b className="text-gray-900">{formatInt(totalSignups)}</b> signups
        </span>
      </div>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.channel}>
            <div className="flex items-baseline justify-between gap-3 text-[11px]">
              <span className="font-semibold text-gray-800">
                {CHANNEL_LABELS[r.channel] ?? r.channel}
              </span>
              <span className="text-gray-500 tabular-nums">
                {formatInt(r.signups)} · {formatPercent(r.share_pct)}
              </span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${r.share_pct ?? 0}%`,
                  backgroundColor: CEDAR.stream,
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
